import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Power, Plus } from "lucide-react";

export default function AdminCoupons() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ code: "", percent_off: 10, max_uses: "", expires_at: "" });

  const load = () => api.get("/api/admin/coupons").then(({data}) => setList(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        percent_off: parseFloat(form.percent_off),
      };
      if (form.max_uses) payload.max_uses = parseInt(form.max_uses);
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      await api.post("/api/admin/coupons", payload);
      toast.success("Coupon created");
      setForm({ code: "", percent_off: 10, max_uses: "", expires_at: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    await api.delete(`/api/admin/coupons/${c.id}`);
    toast.success("Deleted");
    load();
  };

  const toggle = async (c) => {
    await api.post(`/api/admin/coupons/${c.id}/toggle`);
    load();
  };

  return (
    <div data-testid="admin-coupons">
      <div className="label-eyebrow">Coupons</div>
      <h1 className="font-display font-black text-3xl mt-1">Discount coupons</h1>
      <p className="text-sm text-[#4B5563] mt-1">Percentage-off codes customers enter at checkout.</p>

      <form onSubmit={add} className="mt-6 bg-white border border-[#E5E7EB] rounded-xl p-6 grid md:grid-cols-5 gap-3 items-end" data-testid="coupon-form">
        <div>
          <label className="text-sm font-semibold">Code</label>
          <input data-testid="coupon-code" required value={form.code} onChange={(e) => setForm({...form, code: e.target.value})}
            placeholder="WELCOME20" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm uppercase font-mono" />
        </div>
        <div>
          <label className="text-sm font-semibold">% Off</label>
          <input data-testid="coupon-percent" type="number" min={1} max={100} required value={form.percent_off}
            onChange={(e) => setForm({...form, percent_off: e.target.value})}
            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-sm font-semibold">Max uses (blank = ∞)</label>
          <input type="number" min={0} value={form.max_uses} onChange={(e) => setForm({...form, max_uses: e.target.value})}
            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-sm font-semibold">Expires</label>
          <input type="date" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})}
            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
        </div>
        <button data-testid="coupon-add" className="btn-primary flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add</button>
      </form>

      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6]">
            <tr><th className="px-4 py-3 text-left">Code</th><th className="px-4 py-3 text-left">% Off</th><th className="px-4 py-3 text-left">Uses</th><th className="px-4 py-3 text-left">Expires</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-[#E5E7EB]">
                <td className="px-4 py-3 font-mono font-bold">{c.code}</td>
                <td className="px-4 py-3">{c.percent_off}%</td>
                <td className="px-4 py-3">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="px-4 py-3 text-[#4B5563]">{c.expires_at ? c.expires_at.slice(0,10) : "Never"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${c.is_active ? "bg-[#064E3B]/10 text-[#064E3B]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>
                    {c.is_active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => toggle(c)} title="Toggle" data-testid={`coupon-toggle-${c.code}`} className="p-1.5 hover:bg-[#F3F4F6] rounded mr-1">
                    <Power className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(c)} title="Delete" data-testid={`coupon-delete-${c.code}`} className="p-1.5 hover:bg-[#FEE2E2] rounded">
                    <Trash2 className="w-4 h-4 text-[#DC2626]" />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#4B5563]">No coupons yet. Add your first one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
